'use client';

import { api } from '@/api/client';
import { getAdmissionBadgeInfo } from '@/components/blocks/badges/AdmissionBadge';
import { DateFilterButton } from '@/components/blocks/buttons/DateFilterButton';
import { FilterButton } from '@/components/blocks/buttons/FilterButton';
import { ResetButton } from '@/components/blocks/buttons/ResetButton';
import { PaginationBar } from '@/components/blocks/PaginationBar';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { EVENTS_VIEW_MODE_COOKIE, EVENTS_VIEW_MODE_COOKIE_MAX_AGE } from '@/constants/views/events';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useMobile';
import { CenteredPageLayout } from '@/layout/CenteredPageLayout';
import { GridLayout } from '@/layout/GridLayout';
import type { ViewMode } from '@/types';
import { EventCard } from '@/views/events/EventCard';
import { EventCardSkeleton } from '@/views/events/EventCardSkeleton';
import { EventListRow } from '@/views/events/EventListRow';
import { EventListRowSkeleton } from '@/views/events/EventListRowSkeleton';
import { LearnBanner } from '@/views/events/LearnBanner';
import type { Event, PaginatedEvent } from '@mr-website/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { LayoutGrid, List, Monitor } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

const EVENTS_QUERY_KEY = ['events'] as const;

const DESKTOP_PAGE_SIZE = 15;
const MOBILE_PAGE_SIZE = 15;

interface EventsPageProps {
  initialEvents: PaginatedEvent | null;
  initialViewMode: ViewMode;
}

const EventsPage: React.FC<EventsPageProps> = ({ initialEvents, initialViewMode }) => {
  const isMobile = useIsMobile();
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [typePopoverOpen, setTypePopoverOpen] = useState(false);
  const [eventIsOnline, setEventIsOnline] = useState<boolean | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [enableReset, setEnableReset] = useState(false);
  const [viewMode, setViewModeState] = useState<ViewMode>(initialViewMode);
  const setViewMode = (next: ViewMode) => {
    setViewModeState(next);
    document.cookie = `${EVENTS_VIEW_MODE_COOKIE}=${next}; path=/; max-age=${EVENTS_VIEW_MODE_COOKIE_MAX_AGE}; SameSite=Lax`;
  };
  const effectiveViewMode: ViewMode = isMobile ? 'grid' : viewMode;

  const dateRangeKey = [dateRange?.from?.getTime() ?? '', dateRange?.to?.getTime() ?? ''].join('-');

  useEffect(() => {
    setPage(1);
  }, [dateRangeKey, eventIsOnline]);

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE),
    };

    const defaultStart = new Date();
    defaultStart.setHours(0, 0, 0, 0);
    params.event_start_gte = dateRange?.from
      ? dateRange.from.toISOString()
      : defaultStart.toISOString();

    if (dateRange?.to) {
      const endOfDay = new Date(dateRange.to);
      endOfDay.setHours(23, 59, 59, 999);
      params.event_start_lte = endOfDay.toISOString();
    }
    if (eventIsOnline !== undefined) {
      params.event_is_online = eventIsOnline ? 'true' : 'false';
    }
    return params;
  }, [dateRange, eventIsOnline, page, isMobile]);

  const isDefaultQuery = dateRange === undefined && eventIsOnline === undefined && page === 1;

  const baseQueryKey = [
    ...EVENTS_QUERY_KEY,
    dateRange?.from?.toISOString() ?? null,
    dateRange?.to?.toISOString() ?? null,
    eventIsOnline ?? null,
    page,
  ];

  const { data, isLoading, isError } = useQuery({
    queryKey: baseQueryKey,
    queryFn: async () => {
      const { data: body } = await api.get<PaginatedEvent>('/events', { params: queryParams });
      return body;
    },
    initialData: isDefaultQuery && initialEvents ? initialEvents : undefined,
    staleTime: isDefaultQuery ? Infinity : 0,
    placeholderData: keepPreviousData,
  });

  const { isAuthenticated, isResolvingAuth } = useAuth();

  const enrichmentQuery = useQuery({
    queryKey: [...baseQueryKey, 'enrichment'],
    queryFn: async () => {
      const { data: body } = await api.get<PaginatedEvent>('/events', { params: queryParams });
      return body;
    },
    enabled: isDefaultQuery && isAuthenticated && Boolean(initialEvents),
  });
  const { data: enrichmentData } = enrichmentQuery;

  const isPersonalizationPending =
    isDefaultQuery && (isResolvingAuth || (isAuthenticated && !enrichmentQuery.isFetched));

  const events: Event[] = useMemo(() => {
    const base = data?.data ?? [];
    if (!enrichmentData) return base;
    const enrichmentById = new Map(enrichmentData.data.map((event) => [event.id, event]));
    return base.map((event) => {
      const enriched = enrichmentById.get(event.id);
      return enriched
        ? { ...event, user_rsvp: enriched.user_rsvp, crm_link: enriched.crm_link }
        : event;
    });
  }, [data, enrichmentData]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const typeLabel =
    eventIsOnline === true ? 'Online' : eventIsOnline === false ? 'In person' : 'Any type';

  const resetFilters = () => {
    setDateRange(undefined);
    setEventIsOnline(undefined);
    setDatePopoverOpen(false);
    setTypePopoverOpen(false);
    setPage(1);
    setEnableReset(false);
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    setEnableReset(true);
  };

  const setEventType = (next: boolean | undefined) => {
    setEventIsOnline(next);
    setTypePopoverOpen(false);
    setEnableReset(true);
  };

  const renderResults = () => {
    if (isLoading) {
      return effectiveViewMode === 'grid' ? (
        <GridLayout>
          {[0, 1, 2].map((i) => (
            <EventCardSkeleton key={i} />
          ))}
        </GridLayout>
      ) : (
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <EventListRowSkeleton key={i} />
          ))}
        </div>
      );
    }
    if (isError) {
      return <p className="text-sm text-destructive">Could not load events.</p>;
    }
    if (events.length === 0) {
      return <p className="text-sm text-muted-foreground">No events found.</p>;
    }
    if (effectiveViewMode === 'list') {
      return (
        <div className="flex flex-col gap-4">
          {events.map((event: Event) => {
            const { text: badgeText, color: badgeVariant } = getAdmissionBadgeInfo(
              event.is_private,
              event.admission_fee,
            );
            return (
              <EventListRow
                key={event.id}
                event={event}
                badgeText={badgeText}
                badgeVariant={badgeVariant}
                isPersonalizationPending={isPersonalizationPending}
              />
            );
          })}
        </div>
      );
    }
    return (
      <GridLayout>
        {events.map((event: Event) => {
          const { text: badgeText, color: badgeVariant } = getAdmissionBadgeInfo(
            event.is_private,
            event.admission_fee,
          );
          return (
            <EventCard
              key={event.id}
              event={event}
              badgeText={badgeText}
              badgeVariant={badgeVariant}
              className="h-full min-w-0"
              isPersonalizationPending={isPersonalizationPending}
            />
          );
        })}
      </GridLayout>
    );
  };

  return (
    <CenteredPageLayout title="Upcoming Marshall Reddick Events">
      <h2 className="sr-only">Event Listings</h2>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <DateFilterButton
              open={datePopoverOpen}
              onOpenChange={setDatePopoverOpen}
              value={dateRange}
              onValueChange={handleDateRangeChange}
              disabled={{ before: today }}
            />
            <FilterButton
              open={typePopoverOpen}
              onOpenChange={setTypePopoverOpen}
              label={typeLabel}
              onValueChange={setEventType}
              icon={<Monitor />}
            />
            <ResetButton disabled={!enableReset} onClick={resetFilters} />
          </div>
          <ToggleGroup
            value={[viewMode]}
            onValueChange={(groupValue) => {
              const next = groupValue[0] as ViewMode | undefined;
              if (next != null && next !== viewMode) setViewMode(next);
            }}
            variant="outline"
            size="sm"
            aria-label="Event view mode"
            className="hidden md:flex"
          >
            <ToggleGroupItem value="grid" aria-label="Grid view">
              <LayoutGrid className="size-4" aria-hidden />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view">
              <List className="size-4" aria-hidden />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        {renderResults()}
        <PaginationBar
          page={page}
          hasNextPage={data?.has_next_page ?? false}
          onPageChange={setPage}
          pageSize={isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE}
        />
        <LearnBanner />
      </div>
    </CenteredPageLayout>
  );
};

export default EventsPage;
